/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, Component, createRef } from 'react'
import { injectIntl } from 'react-intl'
import { isEmpty } from 'lodash'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGrid,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutHeader,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiIcon,
  EuiSkeletonText,
  EuiOverlayMask,
  EuiPanel,
  EuiSpacer,
  EuiSuperSelect,
  EuiText,
  EuiTitle,
  EuiCallOut,
  EuiButtonGroup,
  htmlIdGenerator,
} from '@elastic/eui'
import type { EuiSuperSelectOption } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  DeploymentTemplateInfoV2,
  InstanceConfiguration,
  DeploymentGetResponse,
  ElasticsearchClusterTopologyElement,
} from '@modules/cloud-api/v1/types'
import { updateDeploymentUrl } from '@modules/cloud-api/v1/urls'
import type { AnyTopologyElement } from '@modules/ui-types'
import { CuiTable } from '@modules/cui/Table'
import { CuiAlert } from '@modules/cui/Alert'

import ApiRequestExample from '@/components/ApiRequestExample'
import DangerButton from '@/components/DangerButton'
import DeploymentConfigurationChangeExplain from '@/components/StackDeploymentConfigurationChange/StackConfigurationChangeExplain/DeploymentConfigurationChangeExplain'
import { isTrialEligibleTemplate } from '@/lib/deploymentTemplates/metadata'
import {
  isLegacyTemplate,
  isTemplateUpgradeAvailable,
  parseVersionFromTemplateId,
} from '@/lib/stackDeployments/selectors/deploymentTemplates'
import { getHourlyRate } from '@/lib/deployments/architecture'
import {
  getNodeConfigurationsFromStackDeployment,
  getDeploymentNodeConfigurations,
} from '@/lib/stackDeployments/selectors/topologyElements'
import { getTopology } from '@/lib/stackDeployments/selectors/fundamentals'
import TotalPrice from '@/components/StackDeploymentEditor/CreateStackDeploymentEditor/PriceButton/TotalPrice'
import { getConfigForKey } from '@/store'

import messages, { getDescriptionText } from '../messages'

// TODO: When ready for prod, delete HardwareComparison.tsx and rename HardwareComparisonPanels.tsx to HardwareComparison.tsx
import HardwareComparison from './HardwareComparison'
import HardwareComparisonPanels from './HardwareComparisonPanels'
import HardwareProfileDropdown from './HardwareProfileDropdown'
import NewVersionBadge from './NewVersionBadge'
import TemplateTitle from './TemplateTitle'
import DryRunCheck from './DryRunCheck'
import TierInstanceConfigurationSelect from './TierInstanceConfigurationSelect'
import {
  applyTierConfigurationsToPayload,
  findMatchingTemplate,
  getDeploymentInstanceConfigurationsByTier,
  getTemplateInstanceConfigurationsByTier,
  type TierInstanceConfigurations,
} from './tierInstanceConfigHelpers'

import type { DataTier } from '@/lib/stackDeployments/selectors/nodeRoles'
import type { AllProps } from './types'

interface State {
  priceViewSelected: 'hourly' | 'monthly'
  isDryRunChecked: boolean
  tierConfigurations: TierInstanceConfigurations
  isCustomProfile: boolean
  showTierDetails: boolean
}

class DeploymentTemplateFlyout extends Component<AllProps, State> {
  state: State = {
    priceViewSelected: `hourly`,
    isDryRunChecked: false,
    tierConfigurations: {},
    isCustomProfile: false,
    showTierDetails: true,
  }

  private errorRef = createRef<HTMLDivElement>()

  componentDidMount() {
    const {
      profile,
      fetchBasePrices,
      basePrices,
      isUserConsole,
      fetchInstanceConfigurations,
      enableTierIcCustomization,
      currentTemplate,
      onChange,
    } = this.props

    if (!basePrices && profile && isUserConsole) {
      fetchBasePrices({
        level: profile.level,
      })
    }

    if (enableTierIcCustomization) {
      fetchInstanceConfigurations()

      if (currentTemplate?.id) {
        onChange(currentTemplate.id)
      }
    }

    // Initialize from deployment's CURRENT ICs (not template defaults) to preserve any previous customizations
    if (enableTierIcCustomization) {
      const { deploymentTemplates } = this.props
      const esTopology = this.getEsTopology()
      const deploymentConfigs = getDeploymentInstanceConfigurationsByTier(esTopology)
      const matchingTemplate = findMatchingTemplate(deploymentConfigs, deploymentTemplates)

      this.setState({ tierConfigurations: deploymentConfigs, isCustomProfile: !matchingTemplate })
    }
  }

  componentDidUpdate(prevProps: AllProps) {
    const { updateDeploymentRequest } = this.props

    if (updateDeploymentRequest.error && !prevProps.updateDeploymentRequest.error) {
      this.errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }

  render() {
    const { isUserConsole, onClose } = this.props
    const flyoutTitleId = htmlIdGenerator()('modalTitle')

    return (
      <EuiOverlayMask headerZindexLocation='below'>
        <EuiFlyout aria-labelledby={flyoutTitleId} maxWidth='65rem' size='l' onClose={onClose}>
          <EuiFlyoutHeader hasBorder={true}>
            <EuiTitle size='m'>
              <h2 id={flyoutTitleId}>
                <FormattedMessage {...messages.editTemplate} values={{ isUserConsole }} />
              </h2>
            </EuiTitle>
            <EuiSpacer size='s' />
            <EuiText color='subdued' size='s'>
              <p>{getDescriptionText()}</p>
            </EuiText>
          </EuiFlyoutHeader>
          {this.renderMigrateContent()}
        </EuiFlyout>
      </EuiOverlayMask>
    )
  }

  renderMigrateContent(): JSX.Element {
    const {
      deployment,
      deploymentTemplates,
      currentTemplate,
      newerTemplate,
      migratedTemplatePayload,
      migrateDeploymentTemplateRequest,
      inTrial,
      onSaveRequest,
      selectedId,
      onChange,
      onSave,
      onClose,
      updateDeploymentRequest,
      regionId,
      isUserConsole,
      isDryRunChecked,
      onDryRunCheckChange,
      isAllowedToUseDeprecatedResources,
      regionInstanceConfigurations,
      enableTierIcCustomization,
      onResetUpdateError,
    } = this.props
    const { isCustomProfile, tierConfigurations, showTierDetails } = this.state
    let options: Array<EuiSuperSelectOption<string>> = []
    const isAdminConsole = getConfigForKey(`APP_NAME`) === 'adminconsole'

    // We need a placeholder item to tell people to choose a value
    const placeholderOptionValue = `placeholder`
    const placeholderOption = {
      value: placeholderOptionValue,
      disabled: true,
      inputDisplay: <FormattedMessage {...messages.placeholderOptionDisplay} />,
    }
    options.push(placeholderOption)

    const currentlySelectedTemplate = deploymentTemplates.find(
      ({ id }) => selectedId && selectedId === id,
    )

    // When tier IC customization is OFF, filter out the current template from dropdown
    // UNLESS it's the newerTemplate (same ID but has version upgrades available)
    const availableTemplates = enableTierIcCustomization
      ? deploymentTemplates
      : deploymentTemplates.filter(
          (template) => template.id !== currentTemplate?.id || template.id === newerTemplate?.id,
        )

    const templateOptions: Array<EuiSuperSelectOption<string>> = availableTemplates.map(
      (template) => {
        const disabled = inTrial && !isTrialEligibleTemplate(template)
        const templateHasVersion = parseVersionFromTemplateId(template.id) !== 0
        const isUpgradeable =
          currentTemplate &&
          isTemplateUpgradeAvailable(currentTemplate.id, template.id) &&
          templateHasVersion
        // If the template is legacy, we want to move people off of it. But the new template might have the same name as the legacy one. So this
        // is likely to be confusing for users. To help out we add a `new version` badge to the template name so they understand this isn't
        // the "legacy" one that we want them to get off of
        const isLegacyTemplateAndHasSameName =
          currentTemplate &&
          isLegacyTemplate(currentTemplate) &&
          currentTemplate.name === template.name

        const showNewBadge =
          !isCustomProfile &&
          (isUpgradeable || newerTemplate?.id === template.id || isLegacyTemplateAndHasSameName)

        return {
          value: template.id,
          disabled,
          inputDisplay: showNewBadge ? (
            <Fragment>
              {template.name} <NewVersionBadge />
            </Fragment>
          ) : (
            template.name
          ),
          dropdownDisplay: (
            <HardwareProfileDropdown
              template={template}
              newBadge={showNewBadge || false}
              isDisabled={disabled}
            />
          ),
        }
      },
    )
    options = options.concat(templateOptions)

    const getSelectedDisplayValue = (): string => {
      if (enableTierIcCustomization && isCustomProfile) {
        return 'custom'
      }

      return selectedId || placeholderOptionValue
    }

    if (enableTierIcCustomization && isCustomProfile && currentlySelectedTemplate) {
      const customLabel = `${currentlySelectedTemplate.name} - Custom`
      const customOption: EuiSuperSelectOption<string> = {
        value: 'custom',
        disabled: false,
        inputDisplay: customLabel,
        dropdownDisplay: (
          <Fragment>
            <strong>{customLabel}</strong>
            <EuiText size='s' color='subdued'>
              <FormattedMessage
                id='deployment-template.custom-profile.description'
                defaultMessage='Custom instance configuration per tier'
              />
            </EuiText>
          </Fragment>
        ),
      }
      options.push(customOption)
    }

    const isDisabled =
      !selectedId ||
      migrateDeploymentTemplateRequest?.inProgress ||
      Boolean(migrateDeploymentTemplateRequest?.error)

    const showHardwareComparison = !isDisabled && migratedTemplatePayload

    return (
      <Fragment>
        <EuiFlyoutBody>
          {newerTemplate && (
            <Fragment>
              <EuiPanel hasBorder={true} hasShadow={false}>
                <EuiTitle size='xxs'>
                  <h4>
                    <EuiIcon type='cheer' style={{ marginRight: `8px` }} />
                    <FormattedMessage
                      {...messages.previewNewVersionTitle}
                      values={{ title: currentTemplate?.name }}
                    />
                  </h4>
                </EuiTitle>
                <EuiSpacer size='m' />
                <EuiText size='s'>
                  <FormattedMessage
                    {...messages.previewNewVersionDescription1}
                    values={{
                      currentTitle: currentTemplate?.name,
                    }}
                  />
                  <br />
                  <FormattedMessage {...messages.previewNewVersionDescription2} />
                </EuiText>
                {!enableTierIcCustomization && (
                  <React.Fragment>
                    <EuiSpacer />
                    <EuiButton onClick={() => onChange(newerTemplate.id)}>
                      <FormattedMessage {...messages.previewNewVersionButton} />
                    </EuiButton>
                  </React.Fragment>
                )}
              </EuiPanel>
              <EuiSpacer />
            </Fragment>
          )}

          <EuiPanel color='subdued' borderRadius='none' hasShadow={false}>
            <EuiFlexGrid columns={2} responsive={false} gutterSize='none'>
              <EuiFlexItem
                style={{ flexBasis: `215px`, maxWidth: `215px`, wordBreak: `break-word` }}
              >
                <EuiText size='xs'>
                  <strong>
                    <FormattedMessage {...messages.currentTemplate} values={{ isUserConsole }} />
                  </strong>
                </EuiText>

                <EuiSpacer size='m' />

                {currentTemplate && (
                  <TemplateTitle
                    template={currentTemplate}
                    style={{ display: `flex`, alignItems: `center`, minHeight: `40px` }}
                  />
                )}
              </EuiFlexItem>
              <EuiFlexItem grow={1}>
                <EuiText size='xs'>
                  <strong>
                    <FormattedMessage {...messages.newTemplate} values={{ isUserConsole }} />
                  </strong>
                </EuiText>

                <EuiSpacer size='m' />

                <EuiSuperSelect
                  fullWidth={true}
                  hasDividers={true}
                  isLoading={migrateDeploymentTemplateRequest?.inProgress}
                  options={options}
                  valueOfSelected={getSelectedDisplayValue()}
                  onChange={(id) => {
                    if (id !== 'custom') {
                      onResetUpdateError?.()

                      const selectedTemplate = deploymentTemplates.find((t) => t.id === id)

                      if (selectedTemplate) {
                        const templateConfigs =
                          getTemplateInstanceConfigurationsByTier(selectedTemplate)

                        this.setState({
                          isCustomProfile: false,
                          tierConfigurations: templateConfigs,
                        })
                      }

                      onChange(id)
                    }
                  }}
                  aria-label='Select hardware profile'
                  data-test-id='migrate-deployment-template-id'
                />
              </EuiFlexItem>
            </EuiFlexGrid>
          </EuiPanel>

          {enableTierIcCustomization && currentlySelectedTemplate && (
            <Fragment>
              <EuiSpacer />
              {regionInstanceConfigurations ? (
                <TierInstanceConfigurationSelect
                  selectedTemplate={currentlySelectedTemplate}
                  instanceConfigurations={regionInstanceConfigurations}
                  deploymentInstanceConfigurations={
                    (deployment as DeploymentGetResponse).instance_configurations
                  }
                  esTopology={this.getEsTopology()}
                  migratedTemplatePayload={migratedTemplatePayload}
                  tierConfigurations={tierConfigurations}
                  onTierConfigurationChange={this.handleTierConfigurationChange}
                  allowLegacyICs={isAllowedToUseDeprecatedResources}
                  showDetails={showTierDetails}
                  onShowDetailsChange={(show) => this.setState({ showTierDetails: show })}
                  isLoading={
                    migrateDeploymentTemplateRequest?.inProgress || !migratedTemplatePayload
                  }
                />
              ) : (
                <EuiSkeletonText lines={6} />
              )}
            </Fragment>
          )}

          {currentTemplate?.id && (
            <Fragment>
              <EuiSpacer />

              {migrateDeploymentTemplateRequest?.inProgress && <EuiSkeletonText />}

              {migrateDeploymentTemplateRequest?.error && (
                <Fragment>
                  <CuiAlert type='error' data-test-id='migrate-deployment-template-error'>
                    {migrateDeploymentTemplateRequest.error}
                  </CuiAlert>
                  <EuiSpacer />
                </Fragment>
              )}

              {showHardwareComparison &&
                (enableTierIcCustomization ? (
                  <HardwareComparisonPanels
                    deployment={deployment}
                    deploymentTemplates={[...deploymentTemplates, currentTemplate]}
                    migratedTemplatePayload={migratedTemplatePayload}
                    showDetails={showTierDetails}
                  />
                ) : (
                  <HardwareComparison
                    deployment={deployment}
                    deploymentTemplates={[...deploymentTemplates, currentTemplate]}
                    migratedTemplatePayload={migratedTemplatePayload}
                  />
                ))}

              {updateDeploymentRequest.error && (
                <div ref={this.errorRef}>
                  <EuiSpacer size='s' />
                  <CuiAlert type='error' data-test-id='update-deployment-template-error'>
                    {updateDeploymentRequest.error}
                  </CuiAlert>
                  <EuiSpacer />
                </div>
              )}
            </Fragment>
          )}

          {isUserConsole && (
            <EuiPanel
              paddingSize='none'
              hasShadow={false}
              hasBorder={false}
              style={{
                position: 'sticky',
                bottom: 0,
              }}
            >
              {this.renderPrices({ currentlySelectedTemplate, regionId })}
            </EuiPanel>
          )}
        </EuiFlyoutBody>

        <EuiFlyoutFooter>
          <EuiFlexGroup justifyContent='spaceBetween'>
            {isAdminConsole && (
              <EuiFlexItem>
                <EuiSpacer size='s' />
                <DryRunCheck
                  isChecked={isDryRunChecked}
                  onDryRunChange={onDryRunCheckChange}
                  label={<FormattedMessage {...messages.dryRunLabel} />}
                  description={<FormattedMessage {...messages.dryRunDescription} />}
                  help={<FormattedMessage {...messages.dryRunHelp} />}
                />
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty onClick={onClose} flush='left'>
                <FormattedMessage {...messages.cancel} />
              </EuiButtonEmpty>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              {enableTierIcCustomization ? (
                <DangerButton
                  onConfirm={() => {
                    const payload = this.getUpdatePayload()

                    if (payload) {
                      onSave(payload)
                    }
                  }}
                  isBusy={onSaveRequest.inProgress}
                  disabled={isDisabled}
                  color='primary'
                  fill={true}
                  data-test-id='migrate-deployment-template-button'
                  modal={{
                    title: <FormattedMessage {...messages.confirmModalTitle} />,
                    body: this.renderConfirmModalBody,
                  }}
                >
                  <FormattedMessage {...messages.update} />
                </DangerButton>
              ) : (
                <EuiButton
                  onClick={() => {
                    const payload = this.getUpdatePayload()

                    if (payload) {
                      onSave(payload)
                    }
                  }}
                  isLoading={onSaveRequest.inProgress}
                  disabled={isDisabled}
                  color='primary'
                  fill={true}
                  data-test-id='migrate-deployment-template-button'
                >
                  <FormattedMessage {...messages.update} />
                </EuiButton>
              )}
              {enableTierIcCustomization && this.getUpdatePayload() && (
                <ApiRequestExample
                  method='PUT'
                  endpoint={updateDeploymentUrl({ deploymentId: deployment.id })}
                  body={this.getUpdatePayload()}
                />
              )}
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlyoutFooter>
      </Fragment>
    )
  }

  renderPrices({
    currentlySelectedTemplate,
    regionId,
  }: {
    currentlySelectedTemplate?: DeploymentTemplateInfoV2
    regionId: string
  }): JSX.Element | null {
    const {
      intl: { formatMessage },
      deployment,
      migratedTemplatePayload,
      basePrices,
      fetchBasePricesRequest,
      regionInstanceConfigurations,
      enableTierIcCustomization,
    } = this.props
    const { tierConfigurations } = this.state
    const { inProgress, error } = fetchBasePricesRequest

    // Don't show anything until a template is selected
    if (!migratedTemplatePayload) {
      return null
    }

    if (!basePrices || !deployment) {
      return <EuiSkeletonText lines={1} />
    }

    if (isEmpty(basePrices) && inProgress && !error) {
      return <EuiSkeletonText lines={1} />
    }

    const payloadForPricing = enableTierIcCustomization
      ? applyTierConfigurationsToPayload({
          migratedTemplatePayload,
          tierConfigurations,
          esTopology: this.getEsTopology(),
          instanceConfigurations: regionInstanceConfigurations,
        })
      : migratedTemplatePayload

    const templateICs = currentlySelectedTemplate?.instance_configurations || []
    const instanceConfigsForPricing = enableTierIcCustomization
      ? [
          ...templateICs,
          ...(regionInstanceConfigurations || []).filter(
            (regionIC) => !templateICs.some((tempIC) => tempIC.id === regionIC.id),
          ),
        ]
      : currentlySelectedTemplate?.instance_configurations

    // we want to be passing in the current deployment's actual instance configurations, not the ones that are _supposed_ to be there from
    // the template. This way if the user has moved away from the template, it will still work.
    const currentPrice = this.getPrices({
      instanceConfigurations: (deployment as DeploymentGetResponse).instance_configurations,
      regionId,
      nodeConfigurations: getNodeConfigurationsFromStackDeployment(deployment),
    })
    const newPrice = this.getPrices({
      instanceConfigurations: instanceConfigsForPricing,
      regionId,
      nodeConfigurations: getDeploymentNodeConfigurations({
        deployment: payloadForPricing,
      }),
    })
    const columns = [
      {
        id: `component`,
        label: ``,
        render: () => {
          const options = [
            {
              id: `hourly`,
              label: <FormattedMessage {...messages.hourlyCostLabel} />,
            },
            {
              id: `monthly`,
              label: <FormattedMessage {...messages.monthlyCostLabel} />,
            },
          ]

          return (
            <EuiButtonGroup
              options={options}
              idSelected={this.state.priceViewSelected}
              onChange={(e) => this.setState({ priceViewSelected: e as 'hourly' | 'monthly' })}
              legend={formatMessage(messages.priceButtonGroupLegend)}
            />
          )
        },
        width: `220px`,
        truncateText: false,
      },
      {
        id: `current`,
        label: ``,
        render: () => this.renderPrice(currentPrice),
        truncateText: false,
      },
      {
        id: `icon`,
        render: ({ isAddition }) =>
          isAddition ? <EuiIcon type='plus' color='success' /> : <EuiIcon type='sortRight' />,
        width: `40px`,
      },
      {
        id: `new`,
        label: ``,
        render: () => this.renderPrice(newPrice),
        truncateText: false,
      },
    ]

    // Bit of a hack here to get this table to work given that there's only ever 1 row. But I'm doing this because it then lines up nicely with the hardware
    // comparison table above. And I didn't want to mash the price info into that table as it's strongly typed to topology elements
    const rows = [{}]

    return <CuiTable columns={columns} rows={rows} hasHeaderRow={false} />
  }

  renderPrice(hourlyRate: number | null): JSX.Element {
    if (!hourlyRate || hourlyRate === 0) {
      return (
        <EuiCallOut announceOnMount={true} color='warning'>
          <FormattedMessage
            id='deployment-template-migrate.price-unavailable'
            defaultMessage='Price unavailable'
          />
        </EuiCallOut>
      )
    }

    return <TotalPrice hourlyRate={hourlyRate} priceViewSelected={this.state.priceViewSelected} />
  }

  renderConfirmModalBody = (): JSX.Element => {
    const { deployment, migratedTemplatePayload, currentTemplate, selectedId } = this.props
    const updatePayload = this.getUpdatePayload()

    if (!updatePayload) {
      return <FormattedMessage {...messages.confirmModalBody} />
    }

    const isTemplateChanging = currentTemplate?.id !== selectedId

    return (
      <Fragment>
        <DeploymentConfigurationChangeExplain
          deployment={updatePayload}
          deploymentUnderEdit={deployment}
          pruneOrphans={migratedTemplatePayload?.prune_orphans ?? false}
        />
        {isTemplateChanging && (
          <Fragment>
            <EuiSpacer size='m' />
            <EuiCallOut
              size='m'
              color='primary'
              iconType='info'
              title={<FormattedMessage {...messages.templateMigrationWarning} />}
              announceOnMount={true}
            />
          </Fragment>
        )}
      </Fragment>
    )
  }

  handleTierConfigurationChange = (tier: DataTier, instanceConfigurationId: string) => {
    const { selectedId, deploymentTemplates, onChange, onResetUpdateError } = this.props

    onResetUpdateError?.()

    const newTierConfigurations = {
      ...this.state.tierConfigurations,
      [tier]: instanceConfigurationId,
    }

    const matchingTemplate = findMatchingTemplate(newTierConfigurations, deploymentTemplates)
    const isCustomProfile = !matchingTemplate

    // If configs match a different template, switch to it and update all tier configs to that template's defaults
    if (matchingTemplate && matchingTemplate.id !== selectedId) {
      const templateConfigs = getTemplateInstanceConfigurationsByTier(matchingTemplate)

      this.setState({
        tierConfigurations: templateConfigs,
        isCustomProfile: false,
      })

      onChange(matchingTemplate.id)
    } else {
      this.setState({
        tierConfigurations: newTierConfigurations,
        isCustomProfile,
      })
    }
  }

  getPrices({
    instanceConfigurations,
    regionId,
    nodeConfigurations,
  }: {
    instanceConfigurations?: InstanceConfiguration[]
    regionId: string
    nodeConfigurations: AnyTopologyElement[]
  }): number | null {
    const { basePrices, profile } = this.props

    if (!basePrices || !profile || !instanceConfigurations) {
      return null
    }

    const { hourlyRate } = getHourlyRate({
      basePrices,
      regionId,
      instanceConfigurations,
      nodeConfigurations,
      level: profile.level,
    })

    return hourlyRate
  }

  getEsTopology(): ElasticsearchClusterTopologyElement[] {
    const { deployment } = this.props
    const esResource = deployment.resources.elasticsearch[0]
    return getTopology({ resource: esResource })
  }

  getUpdatePayload() {
    const { migratedTemplatePayload, regionInstanceConfigurations, enableTierIcCustomization } =
      this.props
    const { tierConfigurations } = this.state

    if (!migratedTemplatePayload) {
      return null
    }

    if (enableTierIcCustomization) {
      return applyTierConfigurationsToPayload({
        migratedTemplatePayload,
        tierConfigurations,
        esTopology: this.getEsTopology(),
        instanceConfigurations: regionInstanceConfigurations,
      })
    }

    return migratedTemplatePayload
  }
}

export default injectIntl(DeploymentTemplateFlyout)
